"use client"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { FormEvent, useState } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { isNumeric } from "../../../../util/string"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { formatCoins } from "../../../../util/format"
import { useSupportContext } from "./SupportSheetDrawer"
import { toast } from "sonner"
import { TX_CHAIN_EXPLORER } from "@/hooks/coins/constants"
import { Address, parseEther } from "viem"
import { useTradeCoins } from "@/hooks/coins/useTradeCoins"
import { useInvalidateSupporters } from "@/hooks/coins/useSupporters"

type Props = {
  initialCoins: string
  coinsPerUSD: number
  ethPrice: number
  tokenAddress: Address
}

export default function Trade({
  initialCoins,
  coinsPerUSD,
  ethPrice,
  tokenAddress,
}: Props) {
  const [coins, setCoins] = useState(String(0))
  const [loading, setLoading] = useState(false)
  const { close } = useSupportContext()
  const tradeCoins = useTradeCoins(tokenAddress)
  const { invalidateAll, invalidateTiers } = useInvalidateSupporters()

  const isValidCoins =
    isNumeric(coins) &&
    parseInt(coins) <= parseInt(initialCoins) &&
    parseFloat(coins) > 0 &&
    parseFloat(coins) === parseInt(coins)

  const handleClick = (percentage: number) => {
    setCoins(String(Math.floor((parseInt(initialCoins) * percentage) / 100)))
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setLoading(true)
    try {
      if (!isValidCoins || !(ethPrice > 0))
        throw new Error("Invalid support amount")

      let amount: bigint = BigInt(coins) * BigInt(1e18)
      // If use wants to trade all its coins, consider the decimals too
      if (String(Math.floor(parseInt(initialCoins))) === coins) {
        amount = parseEther(initialCoins)
      }

      const { txHash, toastId, amountOut } = await tradeCoins("sell", amount)

      await invalidateAll()

      toast.success(
        <div className="flex flex-col gap-1">
          <span>{`Your trade went through — ${formatCoins(Number(coins))} coins exchanged for $${((Number(amountOut) * ethPrice) / 1e18).toFixed(2)}.`}</span>
          <a
            href={`${TX_CHAIN_EXPLORER}${txHash}`}
            target="_blank"
            className="underline"
          >
            View your transaction.
          </a>
        </div>,
        { id: toastId }
      )
    } catch (error) {
      if (/User rejected the request/.test(String(error))) {
        console.error("User rejected the request")
        return
      }
      if (/Not enough balance/.test(String(error))) {
        toast.error("Not enough funds to support", {
          id: parseInt(error as string),
        })
      } else {
        toast.error(
          "We couldn’t complete your trade. Please check your wallet and try again.",
          { id: parseInt(error as string) }
        )
      }
    } finally {
      setLoading(false)
      close()
      await invalidateTiers()
    }
  }

  return (
    <div className="mt-6">
      <div className="w-full flex gap-4 my-6">
        <Card className="flex-1 py-4 px-8">
          <CardContent className="flex flex-col items-center justify-center p-0 gap-1">
            <h3 className="font-normal text-wood-500 text-xs">Balance</h3>
            <span className="font-normal text-sm text-foreground">
              {formatCoins(parseInt(initialCoins))}
            </span>
          </CardContent>
        </Card>

        <Card className="flex-1 py-4 px-8 flex justify-center">
          <Tooltip delayDuration={250}>
            <TooltipTrigger className="cursor-help">
              <CardContent className="flex flex-col items-center justify-center p-0 gap-1">
                <h3 className="font-normal text-wood-500 text-xs">Value</h3>
                <div className="flex flex-col items-center">
                  <span className="font-normal text-sm text-foreground">
                    {`$${Number(Number(initialCoins) / coinsPerUSD).toFixed(2)}`}
                  </span>
                  <span className="font-normal text-xs text-foreground">
                    {`(${Number(Number(initialCoins) / (ethPrice * coinsPerUSD)).toFixed(ETH_DECIMALS)} ETH)`}
                  </span>
                </div>
              </CardContent>
            </TooltipTrigger>

            <TooltipContent className="max-w-[280px] bg-card p-4 z-[51] flex flex-col gap-2 text-card-foreground border rounded-md text-sm">
              This is an approximate value based on the coins you hold and the
              current market price. The actual amount you receive may be lower,
              as the price can change when coins are sold.
            </TooltipContent>
          </Tooltip>
        </Card>
      </div>
      <form onSubmit={handleSubmit}>
        <h3 className="font-normal text-sm text-wood-500">
          How many coins would you like to sell?
        </h3>

        <div className="relative mt-5">
          <Input
            value={coins}
            onChange={(val) => setCoins(val.target.value)}
            className="px-4 py-6 text-xl font-normal border-border"
          />
          <span className="absolute right-2 top-0 pt-[10px] pr-2 text-xl font-normal text-muted-foreground pointer-events-none">
            coins
          </span>
        </div>

        <div className="flex gap-3 my-4">
          <Button
            className="flex-1"
            variant="outline"
            onClick={() => handleClick(25)}
          >
            25%
          </Button>
          <Button
            className="flex-1"
            variant="outline"
            onClick={() => handleClick(50)}
          >
            50%
          </Button>
          <Button
            className="flex-1"
            variant="outline"
            onClick={() => handleClick(75)}
          >
            75%
          </Button>
          <Button
            className="flex-1"
            variant="outline"
            onClick={() => handleClick(100)}
          >
            100%
          </Button>
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={!isValidCoins || loading}
        >
          Trade
        </Button>

        <div className="bg-wood-50 dark:bg-background dark:border-border border-transparent border rounded-lg mt-5 p-3 flex flex-col gap-3">
          <div className="flex w-full justify-between items-center">
            <span className="text-sm font-normal text-muted-foreground">
              Receive
            </span>
            {isValidCoins && (
              <div className="flex items-center gap-1">
                <span className="text-sm font-normal text-foreground">{`$${Number(Number(coins) / coinsPerUSD).toFixed(2)}`}</span>
                <span className="text-sm font-normal text-muted-foreground">
                  {`(${Number(Number(coins) / ethPrice / coinsPerUSD).toFixed(ETH_DECIMALS)} ETH)`}
                </span>
              </div>
            )}
          </div>
          <div className="flex w-full justify-between">
            <span className="text-sm font-normal text-muted-foreground">
              Remaining
            </span>
            <span className="text-sm font-normal text-foreground">
              {isValidCoins || parseFloat(coins) === 0
                ? formatCoins(parseInt(initialCoins) - parseInt(coins))
                : ""}
            </span>
          </div>
        </div>
      </form>
    </div>
  )
}

const ETH_DECIMALS = 5
